import { useEffect, useState } from 'react'
import { analyticsApiService, type TemplateAnalytics } from '../../services/analyticsApiService'
import { ApiError } from '../../services/BaseApiService'
import { colors, spacing } from '../../styles/theme'
import { capitalize, fmtHours, fmtNum, fmtPct, ratio, surfaceLabel } from '../../utils/analyticsFormat'
import {
  BarList, card, DailyChart, EmptyState, ErrorNote, gridAuto, KpiCard, RangePicker, SectionTitle, VIEWS_EMPTY_TEXT,
} from './AnalyticsUi'

export default function TemplateAnalyticsPanel({ templateId }: { templateId: string }) {
  const [days, setDays] = useState(30)
  const [data, setData] = useState<TemplateAnalytics | null>(null)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let live = true
    setLoading(true); setError('')
    analyticsApiService.getTemplate(templateId, days)
      .then(d => { if (live) setData(d) })
      .catch(e => { if (live) setError(e instanceof ApiError ? e.message : 'Failed to load template analytics') })
      .finally(() => { if (live) setLoading(false) })
    return () => { live = false }
  }, [templateId, days])

  const t = data?.totals
  const noViews = !t || t.views === 0

  return (
    <div data-testid="template-analytics">
      <SectionTitle right={<RangePicker days={days} onChange={setDays} />}>Analytics</SectionTitle>
      {error && <ErrorNote message={error} />}
      {loading && !data ? (
        <div style={{ color: colors.textSecondary }}>Loading…</div>
      ) : data && t && (
        <>
          <div style={{ ...gridAuto(140), marginBottom: spacing.lg }}>
            <KpiCard label="Documents" value={fmtNum(t.documents)} />
            <KpiCard label="Pages" value={fmtNum(t.pages)} />
            <KpiCard label="Success rate" value={fmtPct(t.successRate)} tone={t.failed > 0 ? colors.warning : undefined} />
            <KpiCard label="Views" value={fmtNum(t.views)} hint={`${fmtNum(t.downloads)} downloads`} />
            <KpiCard label="Unique viewers" value={fmtNum(t.uniqueViewers)} />
            <KpiCard label="Viewed documents" value={fmtPct(ratio(t.viewedDocuments, t.documents))} hint={`${fmtNum(t.viewedDocuments)} of ${fmtNum(t.documents)}`} />
            <KpiCard label="Avg time to first view" value={fmtHours(t.avgFirstViewHours)} />
          </div>

          <div style={{ ...card, marginBottom: spacing.lg }}>
            <SectionTitle>Documents vs views</SectionTitle>
            {t.documents === 0 && t.views === 0
              ? <EmptyState title="No activity in this period">Render this template to start seeing activity. {VIEWS_EMPTY_TEXT}</EmptyState>
              : <DailyChart rows={data.daily} series={[
                  { key: 'documents', label: 'Documents', color: colors.primary },
                  { key: 'views', label: 'Views', color: colors.info },
                ]} />}
          </div>

          <div style={gridAuto(260)}>
            <div style={card}>
              <SectionTitle>By version</SectionTitle>
              <BarList
                items={data.byVersion.map(v => ({ label: `v${v.versionNumber}`, value: v.documents, sub: v.failed ? `${fmtNum(v.failed)} failed` : undefined }))}
                emptyText="No documents rendered in this period."
              />
            </div>
            <div style={card}>
              <SectionTitle>By surface</SectionTitle>
              {noViews ? <EmptyState title="No views yet">{VIEWS_EMPTY_TEXT}</EmptyState> : (
                <BarList color={colors.info} items={data.bySurface.map(s => ({ label: surfaceLabel(s.surface), value: s.views, sub: `${fmtNum(s.uniqueViewers)} unique` }))} />
              )}
            </div>
            <div style={card}>
              <SectionTitle>Devices</SectionTitle>
              {noViews ? <EmptyState title="No views yet">{VIEWS_EMPTY_TEXT}</EmptyState> : (
                <BarList color={colors.info} items={data.devices.map(d => ({ label: capitalize(d.device), value: d.views }))} />
              )}
            </div>
          </div>
        </>
      )}
    </div>
  )
}
